'use strict';

/**
 * URL serialization helpers for reporting configuration
 * URL serialization helpers with length safety
 */

// URL length limits for modern Chrome (supports up to 2MB URLs)
const MAX_QUERY_LENGTH = 2000000; // 2MB limit for modern Chrome
const WARNING_THRESHOLD = 100000; // Warn when approaching 100KB

/**
 * Serialize reporting selections to URL query parameters
 * @param {Object} selections - Reporting selections
 * @param {string[]} selections.workpackageIds - Selected workpackage IDs
 * @param {string[]} selections.attributeKeys - Selected attribute keys
 * @param {string} selections.approvalStatus - Approval status
 * @param {Object} options - Serialization options
 * @param {number} options.maxWorkpackages - Max workpackages to include
 * @param {number} options.maxAttributes - Max attributes to include
 * @returns {Object} Query parameters object
 */
export function serializeReportingSelections(selections, { maxWorkpackages, maxAttributes }) {
  const { workpackageIds = [], attributeKeys = [], approvalStatus = 'notApproved' } = selections;

  // Apply limits to prevent excessive URL length
  const limitedWorkpackageIds = workpackageIds.slice(0, maxWorkpackages);
  const limitedAttributeKeys = attributeKeys.slice(0, maxAttributes);

  const query = {
    wp: limitedWorkpackageIds.join(','),
    attr: limitedAttributeKeys.join(','),
    status: approvalStatus,
  };

  // Check URL length and warn if necessary
  const queryString = new URLSearchParams(query).toString();

  if (queryString.length > MAX_QUERY_LENGTH) {
    console.error(
      `URL length (${
        queryString.length
      }) exceeds limit (${MAX_QUERY_LENGTH}). Consider reducing selections.`
    );
  } else if (queryString.length > WARNING_THRESHOLD) {
    console.warn(`URL length (${queryString.length}) is large. Consider reducing selections.`);
  }

  return query;
}

/**
 * Deserialize URL query parameters to reporting selections
 * @param {Object} query - URL query parameters
 * @param {Object} options - Deserialization options
 * @param {number} options.maxWorkpackages - Max workpackages to parse
 * @param {number} options.maxAttributes - Max attributes to parse
 * @returns {Object} Reporting selections
 */
export function deserializeReportingSelections(query, { maxWorkpackages, maxAttributes }) {
  const wpParam = query.wp || '';
  const attrParam = query.attr || '';
  const statusParam = query.status || 'notApproved';

  const rawWorkpackageIds = wpParam ? wpParam.split(',').filter(Boolean) : [];
  const rawAttributeKeys = attrParam ? attrParam.split(',').filter(Boolean) : [];

  // Apply limits
  const workpackageIds = rawWorkpackageIds.slice(0, maxWorkpackages);
  const attributeKeys = rawAttributeKeys.slice(0, maxAttributes);

  return {
    workpackageIds,
    attributeKeys,
    approvalStatus: statusParam,
  };
}
